import React, { useEffect, useState } from "react";
import "../App.css";
import { apiRequest, API_ENDPOINTS, getCurrentUser, hasSafeAccess } from "../utils/api";
import { Navigate } from "react-router-dom";

export default function Safe() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [transactions, setTransactions] = useState([]);
    const [totalBalance, setTotalBalance] = useState(0);

    const [form, setForm] = useState({
        type: "in",
        amount: "",
        description: ""
    });
    const [submitting, setSubmitting] = useState(false);

    const user = getCurrentUser();

    const loadSafeData = async () => {
        setLoading(true);
        setError("");
        try {
            const data = await apiRequest(API_ENDPOINTS.SAFE);
            setTransactions(data.transactions || []);
            setTotalBalance(Number(data.total_balance) || 0);
        } catch (err) {
            setError(err.message || "Failed to load safe data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadSafeData();
    }, []);

    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        const amount = parseFloat(form.amount.replace(",", "."));
        if (isNaN(amount) || amount <= 0) {
            setError("Amount must be a positive number.");
            return;
        }

        setSubmitting(true);
        try {
            await apiRequest(API_ENDPOINTS.SAFE, {
                method: "POST",
                body: JSON.stringify({
                    type: form.type,
                    amount,
                    description: form.description
                })
            });
            setSuccess("Safe transaction saved.");
            setForm({ type: form.type, amount: "", description: "" });
            await loadSafeData();
        } catch (err) {
            setError(err.message || "Failed to save transaction.");
        } finally {
            setSubmitting(false);
        }
    };

    const formatAmount = (value) => {
        const num = Number(value);
        if (isNaN(num)) return "0.00";
        return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    if (!hasSafeAccess()) {
        return <Navigate to="/home" replace />;
    }

    return (
        <div className="safe-page">
            <div className="safe-header">
                <div className="safe-header-text">
                    <h1>Safe (Cash Vault)</h1>
                    <p>Track all cash in and out, by user and date.</p>
                </div>
                <div
                    className={`safe-balance-shell ${totalBalance < 0 ? "is-negative" : "is-positive"}`}
                    aria-live="polite"
                    key={totalBalance}
                >
                    <div className="safe-balance-card">
                        <span className="safe-balance-label">Total balance</span>
                        <span className="safe-total">
                            $ {formatAmount(totalBalance)}
                        </span>
                    </div>
                </div>
            </div>

            {error && <div className="alert error">{error}</div>}
            {success && <div className="alert success">{success}</div>}

            <div className="safe-summary">
                <div className="safe-form-card">
                    <h3>New transaction</h3>
                    <form className="safe-form" onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label>Type</label>
                            <select
                                value={form.type}
                                onChange={(e) => handleChange("type", e.target.value)}
                            >
                                <option value="in">Cash in</option>
                                <option value="out">Cash out</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Amount</label>
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={form.amount}
                                onChange={(e) => handleChange("amount", e.target.value)}
                                placeholder="0.00"
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Description (optional)</label>
                            <input
                                type="text"
                                value={form.description}
                                onChange={(e) => handleChange("description", e.target.value)}
                                placeholder="e.g. Cash deposit, rent payout..."
                            />
                        </div>
                        <div className="form-group form-group-btn">
                            <button
                                type="submit"
                                className="btn-primary"
                                disabled={submitting}
                            >
                                {submitting ? "Saving..." : "Save transaction"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <div className="safe-table-card">
                <div className="safe-table-header">
                    <h3>History</h3>
                    {transactions.length > 0 && (
                        <button
                            type="button"
                            className="btn-danger sm"
                            onClick={async () => {
                                if (!window.confirm("Are you sure you want to reset the safe balance and delete all transactions?")) {
                                    return;
                                }
                                if (!window.confirm("This cannot be undone. Do you really want to reset everything?")) {
                                    return;
                                }
                                setError("");
                                setSuccess("");
                                try {
                                    await apiRequest(API_ENDPOINTS.SAFE, { method: "DELETE" });
                                    setTransactions([]);
                                    setTotalBalance(0);
                                    setSuccess("Safe balance reset and all transactions removed.");
                                } catch (err) {
                                    setError(err.message || "Failed to reset safe.");
                                }
                            }}
                        >
                            Reset safe
                        </button>
                    )}
                </div>
                {loading ? (
                    <div className="loading">Loading safe history...</div>
                ) : transactions.length === 0 ? (
                    <div className="no-data">
                        <p>No transactions yet. Add a first entry above.</p>
                    </div>
                ) : (
                    <div className="safe-table-wrap">
                        <table className="safe-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>User</th>
                                    <th>Type</th>
                                    <th>Amount</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                {transactions.map((tx) => (
                                    <tr key={tx.id}>
                                        <td className="cell-nowrap">{new Date(tx.created_at).toLocaleString()}</td>
                                        <td>{tx.username}</td>
                                        <td>
                                            <span className={`safe-type ${tx.type}`}>
                                                {tx.type === "in" ? "Cash in" : "Cash out"}
                                            </span>
                                        </td>
                                        <td className={tx.type === "out" ? "amount-out" : "amount-in"}>
                                            {tx.type === "out" ? "-" : "+"} $ {formatAmount(tx.amount)}
                                        </td>
                                        <td>{tx.description || "-"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

